import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, use, useCallback, useMemo, type ReactNode } from 'react'
import { ApiProblem, ConsoleClient, type Session } from '../api/client'

/**
 * The session boundary.
 *
 * Everything the console renders for a signed-in person hangs off this. It
 * holds no token and writes no storage: the session is an HttpOnly cookie the
 * browser sends by itself, and anything this code could read, it could leak.
 */

export type SessionState =
  | { readonly status: 'loading' }
  | { readonly status: 'signedOut'; readonly loginUrl: string }
  | { readonly status: 'denied' }
  | { readonly status: 'unavailable' }
  | {
      readonly status: 'signedIn'
      readonly generation: string
      readonly displayName: string
      readonly tenantName: string
      readonly capabilities: readonly string[]
      readonly csrfToken: string
    }

interface SessionContextValue {
  readonly state: SessionState
  readonly signOut: () => Promise<void>
  readonly can: (capability: string) => boolean
  /** The API client, for components that read data beyond the session. */
  readonly client: ConsoleClient
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined)

/** The query key. One entry, so a login or logout can clear it by name. */
const SESSION_KEY = ['session'] as const

export function SessionProvider({
  client,
  children,
  queryClient,
}: {
  client: ConsoleClient
  children: ReactNode
  queryClient?: QueryClient
}) {
  const configured = useMemo(() => queryClient ?? defaultQueryClient(), [queryClient])

  return (
    <QueryClientProvider client={configured}>
      <SessionBoundary client={client}>{children}</SessionBoundary>
    </QueryClientProvider>
  )
}

function SessionBoundary({ client, children }: { client: ConsoleClient; children: ReactNode }) {
  const queries = useQueryClient()

  const { data, error, isPending } = useQuery({
    queryKey: SESSION_KEY,
    queryFn: ({ signal }) => client.session(signal),
  })

  const state = useMemo(() => toState(data, error, isPending), [data, error, isPending])

  const signOut = useCallback(async () => {
    const token = state.status === 'signedIn' ? state.csrfToken : ''

    let destination: string | undefined
    try {
      destination = (await client.logout(token)).logout_url
    } catch {
      // Swallowed on purpose, and this is the whole reason logout is designed
      // to be idempotent. There is no recovery a person could perform and no
      // message worth showing: what matters is that this browser stops holding
      // the previous identity, which the clear below does regardless. Rethrowing
      // would leave them looking at a signed-in console with an error on it.
    } finally {
      // Cleared whether or not logout succeeded. Whatever happened server-side,
      // this browser must not keep rendering the previous identity's data while
      // the next person signs in.
      await queries.cancelQueries()
      queries.clear()
    }

    if (destination !== undefined) {
      // A navigation the browser performs itself, so Cognito sees it and ends
      // its own session. See ConsoleClient.logout for why a redirect cannot.
      window.location.assign(destination)
    }
  }, [client, queries, state])

  const can = useCallback(
    (capability: string) => state.status === 'signedIn' && state.capabilities.includes(capability),
    [state],
  )

  const value = useMemo(() => ({ state, signOut, can, client }), [state, signOut, can, client])

  return <SessionContext value={value}>{children}</SessionContext>
}

function toState(data: Session | undefined, error: unknown, isPending: boolean): SessionState {
  if (error instanceof ApiProblem) {
    // 403 means authenticated and not approved, which is a different page from
    // signed out: telling someone to sign in when they already have sends them
    // round a loop that cannot end.
    if (error.status === 403 || error.code === 'access_denied') {
      return { status: 'denied' }
    }
    return { status: 'unavailable' }
  }
  if (error !== null && error !== undefined) {
    return { status: 'unavailable' }
  }
  if (isPending || data === undefined) {
    return { status: 'loading' }
  }

  if (!data.authenticated) {
    return { status: 'signedOut', loginUrl: data.login_url }
  }

  return {
    status: 'signedIn',
    generation: data.session_generation,
    displayName: data.user.display_name,
    tenantName: data.tenant.display_name,
    capabilities: data.capabilities,
    csrfToken: data.csrf_token,
  }
}

/**
 * Reads the session.
 *
 * Throws outside a provider rather than returning a default. A default would
 * make a component that forgot the provider render as signed out, which looks
 * like a session bug and is a wiring bug.
 */
export function useSession(): SessionContextValue {
  const value = use(SessionContext)
  if (value === undefined) {
    throw new Error('useSession must be used inside a SessionProvider')
  }
  return value
}

function defaultQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Nothing survives a reload. Findings are per-principal and the cache
        // is memory-only for the same reason nothing is written to storage.
        gcTime: 0,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // A policy on the client, not on one query. A 401 or 403 is an ANSWER,
        // not a failure worth retrying: retrying an authorization decision only
        // delays telling the person what happened. Set here so a caller can
        // replace it - a test asserting the unavailable state should not have
        // to wait out a backoff to see it.
        retry: (failureCount: number, cause: unknown) =>
          !(cause instanceof ApiProblem && cause.status < 500) && failureCount < 2,
      },
    },
  })
}
